import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type WebsiteActivityResult } from '@whinerp/client';
import { formatCount, formatDateTime } from '@whinerp/contracts/format';
import { Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useFormat, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/**
 * Website activity on a contact or lead (F-153): each session one line - pages
 * and time - expanding to the pages in place. Includes what they read before
 * they identified themselves.
 */
export function WebsiteActivityPanel({ objectKey, recordId }: { objectKey: 'contact' | 'lead'; recordId: string }): JSX.Element | null {
  const t = useT();
  const format = useFormat();
  const enabled = useEntitlement('F-153');
  const [open, setOpen] = useState<string | null>(null);
  const activity = useQuery<WebsiteActivityResult, ApiError>({
    queryKey: ['website-activity', objectKey, recordId],
    queryFn: () => api.websiteActivity({ path: { objectKey, recordId } }),
    enabled: enabled.enabled,
  });
  if (!enabled.enabled) return null;
  if (activity.isPending) return <Skeleton className="h-block-sm w-full" />;
  if (activity.isError || activity.data.sessions.length === 0) return null;
  return (
    <Panel data-region="website-activity">
      <div className="flex flex-col gap-1">
        <SectionLabel>{t('web.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">
          {t('web.seen', { values: { first: formatDateTime(activity.data.firstSeen ?? '', format), last: formatDateTime(activity.data.lastSeen ?? '', format), sessions: formatCount(activity.data.sessions.length, format) } })}
        </p>
        <ul className="flex flex-col">
          {activity.data.sessions.map((session) => (
            <li key={session.sessionId} data-web-session={session.sessionId} className="border-t border-t-rule py-1 first:border-t-0">
              <button
                type="button"
                data-web-session-toggle
                aria-expanded={open === session.sessionId}
                onClick={() => setOpen((current) => (current === session.sessionId ? null : session.sessionId))}
                className="flex w-full flex-wrap items-center gap-2 text-start text-dense text-ink"
              >
                <span>{formatDateTime(session.startedAt, format)}</span>
                <span className="text-meta text-ink-meta">
                  {t('web.sessionLine', { values: { pages: formatCount(session.pageCount, format), minutes: formatCount(Math.max(1, Math.round(session.seconds / 60)), format) } })}
                </span>
                {session.referrer ? <span className="text-meta text-ink-meta">{t('web.from', { values: { referrer: session.referrer } })}</span> : null}
              </button>
              {open === session.sessionId ? (
                <ol data-web-pages className="flex flex-col gap-1 ps-4 pt-1">
                  {session.pages.map((page, index) => (
                    <li key={`${page.viewedAt}-${String(index)}`} className="break-all text-meta text-ink-600">
                      {page.title ?? page.url} {page.seconds !== null ? `· ${formatCount(page.seconds, format)} s` : ''}
                    </li>
                  ))}
                </ol>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </Panel>
  );
}
